import React, { useMemo, useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { Calendar, Sparkles, Clock, Sun, Moon, Compass, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { calculateAuthoritativePanchanga } from '../utils/panchangaEngine';

interface PanchangMuhurtaViewProps {
  kundali?: CompleteKundali | null;
  lang?: Language;
}

export const PanchangMuhurtaView: React.FC<PanchangMuhurtaViewProps> = ({ kundali, lang = 'en' }) => {
  const isHi = lang === 'hi';
  const lagnaSign = kundali?.houses?.[0]?.sign || 'Aries';
  
  // Today's Live Authoritative Panchanga for current location context
  const todayPanchang = useMemo(() => {
    return calculateAuthoritativePanchanga(new Date(), {
      latitude: kundali?.birthDetails?.latitude || 28.6139,
      longitude: kundali?.birthDetails?.longitude || 77.2090,
      elevationMeters: 0,
      timezoneOffsetMinutes: kundali?.birthDetails?.timezoneOffsetMinutes ?? 330,
      placeName: kundali?.birthDetails?.placeName || 'New Delhi, India',
      profileStorageId: kundali?.id || kundali?.cloudId
    });
  }, [kundali]);

  // Natal Panchang from Kundali
  const birthNakshatra = kundali?.panchang?.nakshatra || 'Ashwini';
  const birthTithi = kundali?.panchang?.tithi || 'Shukla Purnima';
  const birthYoga = kundali?.panchang?.yoga || 'Siddhi';
  const birthKarana = kundali?.panchang?.karana || 'Bava';
  const birthVara = kundali?.panchang?.vara || 'Sunday';

  const [viewScope, setViewScope] = useState<'today' | 'birth'>('today');

  // Lagna-specific muhurta guidance
  const getLagnaMuhurtaAdvice = (sign: string) => {
    switch (sign) {
      case 'Aries':
      case 'मेष':
        return {
          business: isHi ? 'नेतृत्व, तकनीकी उद्यम, और खेलकूद से जुड़े कार्यों के लिए उत्तम समय।' : 'Ideal for leadership ventures, tech startups, engineering projects, and physical initiatives.',
          travel: isHi ? 'पूर्व और दक्षिण दिशा में यात्रा सफल।' : 'Favorable for travel towards East and South directions.',
          investment: isHi ? 'रियल एस्टेट और भूमि में निवेश के लिए अनुकूल।' : 'Highly favorable for land, real estate, and capital machinery investments.'
        };
      case 'Taurus':
      case 'वृषभ':
        return {
          business: isHi ? 'वित्त, कला, आभूषण, और खान-पान के व्यापार के लिए श्रेष्ठ।' : 'Exceptional for finance, arts, luxury goods, fashion, banking, and food enterprises.',
          travel: isHi ? 'उत्तर और पश्चिम दिशा में यात्रा शुभ।' : 'Favorable for travel towards North and West directions.',
          investment: isHi ? 'गोल्ड, शेयर बाजार, और फिक्स्ड एसेट्स में निवेश लाभदायक।' : 'Favorable for gold, mutual funds, secure bonds, and immovable property.'
        };
      case 'Gemini':
      case 'मिथुन':
        return {
          business: isHi ? 'संचार, मीडिया, सॉफ्टवेयर, और व्यापारिक अनुबंधों के लिए बेहतरीन।' : 'Outstanding for communication, media, software development, consulting, and trade agreements.',
          travel: isHi ? 'व्यापारिक यात्राओं और सम्मेलनों के लिए उत्तम।' : 'Excellent for business trips, workshops, and educational travel.',
          investment: isHi ? 'शॉर्ट-टर्म ट्रेडिंग और डिजिटल एसेट्स में सूझबूझ से निवेश।' : 'Favorable for short-term growth portfolios and technology funds.'
        };
      case 'Cancer':
      case 'कर्क':
        return {
          business: isHi ? 'आतिथ्य, तरल पदार्थ, खाद्य वस्तुएं, और जन-कल्याण कार्य।' : 'Propitious for hospitality, liquids, food & dairy, real estate, and public care services.',
          travel: isHi ? 'जल मार्ग और पारिवारिक यात्राओं के लिए शुभ।' : 'Favorable for domestic travel and family pilgrimages.',
          investment: isHi ? 'पारिवारिक संपत्ति और सुरक्षित सरकारी बॉन्ड में निवेश।' : 'Favorable for residential property and government securities.'
        };
      case 'Leo':
      case 'सिंह':
        return {
          business: isHi ? 'सरकारी अनुबंध, प्रबंधन, राजनीति, और प्रशासनिक कार्य।' : 'Highly auspicious for government contracts, administrative roles, gold, and corporate leadership.',
          travel: isHi ? 'पूर्व दिशा की आधिकारिक यात्राओं के लिए श्रेष्ठ।' : 'Superior for official tours and visits to authority figures in Eastern directions.',
          investment: isHi ? 'सोना, सरकारी सिक्योरिटीज, और ब्लू-चिप शेयरों में निवेश।' : 'Favorable for gold, blue-chip stocks, and sovereign bonds.'
        };
      case 'Virgo':
      case 'कन्या':
        return {
          business: isHi ? 'लेखा, चिकित्सा, संपादन, और सलाहकार सेवाएं।' : 'Excellent for accounting, auditing, medical consulting, editing, and professional services.',
          travel: isHi ? 'व्यावसायिक और शैक्षणिक यात्राओं के लिए अनुकूल।' : 'Favorable for professional seminars and training travel.',
          investment: isHi ? 'म्यूचुअल फंड, डेट सिक्योरिटीज, और बचत योजनाओं में निवेश।' : 'Favorable for structured savings, debt funds, and diversified portfolios.'
        };
      case 'Libra':
      case 'तुला':
        return {
          business: isHi ? 'साझेदारी, सौंदर्य प्रसाधन, कानूनी सेवाएं, और फैशन।' : 'Favorable for legal partnerships, cosmetics, hospitality, interior design, and luxury commerce.',
          travel: isHi ? 'दक्षिण और पश्चिम दिशा की यात्राएं लाभप्रद।' : 'Profitable for travel towards South and Western regions.',
          investment: isHi ? 'कलाकृतियां, फैशन ब्रांड, और संयुक्त उद्यम (Joint Ventures).' : 'Favorable for art investments, luxury assets, and partnership ventures.'
        };
      case 'Scorpio':
      case 'वृश्चिक':
        return {
          business: isHi ? 'शोध (Research), बीमा, रसायन, और गुप्त परियोजनाओं के लिए।' : 'Exceptional for research & development, insurance, diagnostics, chemicals, and mining.',
          travel: isHi ? 'सावधानीपूर्वक यात्रा करें; गुप्त कार्यों के लिए अनुकूल।' : 'Requires cautious transit; favorable for investigative or confidential research trips.',
          investment: isHi ? 'बीमा पॉलिसियों, पेटेंट, और कमोडिटी फ्यूचर्स में निवेश।' : 'Favorable for insurance, commodities, and high-yield strategic assets.'
        };
      case 'Sagittarius':
      case 'धनु':
        return {
          business: isHi ? 'उच्च शिक्षा, प्रकाशन, कानूनी सलाह, और आध्यात्मिक संस्थान।' : 'Outstanding for higher education, publishing, publishing houses, advisory, and spiritual ventures.',
          travel: isHi ? 'तीर्थयात्राएं और दूरस्थ विदेश यात्राओं के लिए सर्वोत्तम।' : 'Exceptional for long-distance international travel and pilgrimage tours.',
          investment: isHi ? 'गोल्ड, एजुकेशनल ट्रस्ट, और लॉन्ग-टर्म ग्रोथ फंड्स।' : 'Favorable for sovereign gold bonds, educational endowments, and long-term equities.'
        };
      case 'Capricorn':
      case 'मकर':
        return {
          business: isHi ? 'निर्माण (Construction), उद्योग, भारी मशीनरी, और कॉर्पोरेट।' : 'Propitious for heavy manufacturing, infrastructure, logistics, mining, and large enterprise management.',
          travel: isHi ? 'पश्चिम दिशा की व्यावसायिक यात्राओं के लिए उत्तम।' : 'Favorable for business journeys towards Western regions.',
          investment: isHi ? 'भूमि, कमर्शियल रियल एस्टेट, और इन्फ्रास्ट्रक्चर बॉन्ड्स।' : 'Favorable for commercial real estate, industrial property, and infrastructure funds.'
        };
      case 'Aquarius':
      case 'कुंभ':
        return {
          business: isHi ? 'प्रौद्योगिकी, एआई, अनुसंधान, और सामाजिक संगठन।' : 'Ideal for advanced technology, AI ventures, research labs, social initiatives, and aviation.',
          travel: isHi ? 'उत्तरी दिशा की यात्राएं और तकनीकी सेमिनार।' : 'Favorable for travel to tech hubs and international conferences.',
          investment: isHi ? 'टेक स्टार्टअप्स, इनोवेशन फंड्स, और डिजिटल एसेट्स।' : 'Favorable for tech innovations, venture capital, and future-forward assets.'
        };
      case 'Pisces':
      case 'मीन':
        return {
          business: isHi ? 'आयात-निर्यात, चिकित्सा, फार्मा, और आध्यात्मिक सेवाएं।' : 'Favorable for import-export, pharmaceuticals, holistic healing, marine ventures, and charities.',
          travel: isHi ? 'विदेश यात्राओं और जल यात्राओं के लिए अनुकूल।' : 'Favorable for overseas travel and spiritual retreats.',
          investment: isHi ? 'फार्मास्युटिकल्स, चैरिटेबल ट्रस्ट, और लिक्विड फंड्स।' : 'Favorable for healthcare, ethical funds, and charitable endowments.'
        };
      default:
        return {
          business: isHi ? 'सभी प्रकार के नए उद्यमों और वाणिज्यिक कार्यों के लिए।' : 'Favorable for general commerce, trade, and professional initiatives.',
          travel: isHi ? 'शुभ चौघड़िया में यात्रा करना उत्तम रहेगा।' : 'Favorable during auspicious Choghadiya time windows.',
          investment: isHi ? 'संतुलित पोर्टफोलियो और सुरक्षित निवेश।' : 'Favorable for diversified portfolios and steady financial growth.'
        };
    }
  };

  const advice = getLagnaMuhurtaAdvice(lagnaSign);

  const displayedTithi = viewScope === 'today' ? todayPanchang.tithi.name : birthTithi;
  const displayedNakshatra = viewScope === 'today' ? todayPanchang.nakshatra.name : birthNakshatra;
  const displayedYoga = viewScope === 'today' ? todayPanchang.yoga.name : birthYoga;
  const displayedKarana = viewScope === 'today' ? todayPanchang.karana.name : birthKarana;
  const displayedVara = viewScope === 'today' 
    ? `${todayPanchang.vara.nameSanskrit} (${todayPanchang.vara.nameEn})` 
    : birthVara;

  return (
    <div className="space-y-6">
      {/* Header card with Scope Switcher */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 border-b border-astro-border pb-3">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-astro-primary font-serif">
                {isHi ? 'दैनिक पंचांग एवं लग्न आधारित मुहूर्त' : 'Daily Panchang & Lagna-Tailored Auspicious Muhurtas'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi 
                  ? `आपकी लग्न (${lagnaSign}) और जन्म नक्षत्र (${birthNakshatra}) के आधार पर काल-शुद्धि विश्लेषण` 
                  : `Specialized astrological timing based on your ${lagnaSign} Lagna & ${birthNakshatra} Janma Nakshatra`}
              </p>
            </div>
          </div>

          {/* Temporal Scope Switcher: Today's Live vs Birth Janma Panchanga */}
          <div className="flex items-center gap-1.5 p-1 bg-astro-card border border-astro-border rounded-xl text-xs">
            <button
              type="button"
              onClick={() => setViewScope('today')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                viewScope === 'today'
                  ? 'bg-astro-primary text-astro-bg shadow-sm font-semibold'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? 'आज का पंचांग (Live)' : "Today's Live Panchang"}
            </button>
            <button
              type="button"
              onClick={() => setViewScope('birth')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all ${
                viewScope === 'birth'
                  ? 'bg-astro-primary text-astro-bg shadow-sm font-semibold'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? 'जन्म पंचांग (Janma)' : 'Birth Janma Panchang'}
            </button>
          </div>
        </div>

        {/* Temporal Context Badge */}
        <div className="mb-4 flex items-center justify-between text-xs text-astro-secondary-text bg-astro-card/50 px-3 py-2 rounded-xl border border-astro-border/70">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-astro-primary" />
            <span className="font-semibold text-astro-text">
              {viewScope === 'today' ? "Today's Astrological Context:" : "Natal Birth Moment Context:"}
            </span>
            <span>
              {viewScope === 'today' 
                ? `${todayPanchang.samvatString} • ${displayedVara}`
                : `${kundali?.birthDetails?.date || 'N/A'} • ${kundali?.birthDetails?.time || 'N/A'} • ${displayedVara}`}
            </span>
          </div>
          <span className="text-[10px] font-mono text-astro-accent">
            {kundali?.birthDetails?.placeName || 'Reference Location'}
          </span>
        </div>

        {/* Pitru Paksha Contextual Notice (When Active) */}
        {todayPanchang.pitruPaksha?.isActive && viewScope === 'today' && (
          <div className="mb-4 p-3 rounded-xl bg-amber-100/70 border border-amber-300 text-xs flex items-start gap-2.5 animate-fade-in">
            <Sparkles className="w-4 h-4 text-amber-800 shrink-0 mt-0.5" />
            <div>
              <strong className="text-amber-900 font-serif block text-sm">
                {isHi ? 'पितृ पक्ष (श्राद्ध पक्ष) सक्रिय' : 'Pitru Paksha (Shraddha Paksha) Active'}
              </strong>
              <span className="text-xs text-amber-800 block mt-1">
                {todayPanchang.pitruPaksha.description} {todayPanchang.pitruPaksha.applicabilityNote}
              </span>
            </div>
          </div>
        )}

        {/* Panchang Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-astro-card border border-astro-border rounded-xl p-3.5">
            <span className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text block mb-1">
              {isHi ? 'तिथि (संक्रमण)' : 'Tithi (Transitions)'}
            </span>
            {viewScope === 'today' && todayPanchang.tithiTransitions && todayPanchang.tithiTransitions.length > 1 ? (
              <div className="space-y-1">
                {todayPanchang.tithiTransitions.map((tr, tIdx) => (
                  <div key={tIdx} className="text-xs">
                    <span className="font-bold text-astro-text font-serif block">{tr.name}</span>
                    <span className="text-[10px] text-astro-secondary-text font-mono block">{tr.formattedStartTime} – {tr.formattedEndTime}</span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <span className="text-sm font-bold text-astro-text font-serif">{displayedTithi}</span>
                {viewScope === 'today' && todayPanchang.tithi.formattedEndTime && (
                  <span className="text-[10px] text-astro-secondary-text block mt-1 font-mono">
                    until {todayPanchang.tithi.formattedEndTime}
                  </span>
                )}
              </>
            )}
          </div>

          <div className="bg-astro-card border border-astro-border rounded-xl p-3.5">
            <span className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text block mb-1">
              {isHi ? 'नक्षत्र' : 'Nakshatra'}
            </span>
            <span className="text-sm font-bold text-astro-primary font-serif">{displayedNakshatra}</span>
            {viewScope === 'today' && todayPanchang.nakshatra.formattedEndTime && (
              <span className="text-[10px] text-astro-secondary-text block mt-1 font-mono">
                until {todayPanchang.nakshatra.formattedEndTime}
              </span>
            )}
          </div>

          <div className="bg-astro-card border border-astro-border rounded-xl p-3.5">
            <span className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text block mb-1">
              {isHi ? 'योग' : 'Yoga'}
            </span>
            <span className="text-sm font-bold text-astro-text font-serif">{displayedYoga}</span>
            {viewScope === 'today' && todayPanchang.yoga.formattedEndTime && (
              <span className="text-[10px] text-astro-secondary-text block mt-1 font-mono">
                until {todayPanchang.yoga.formattedEndTime}
              </span>
            )}
          </div>

          <div className="bg-astro-card border border-astro-border rounded-xl p-3.5">
            <span className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text block mb-1">
              {isHi ? 'करण' : 'Karana'}
            </span>
            <span className="text-sm font-bold text-astro-text font-serif">{displayedKarana}</span>
            {viewScope === 'today' && todayPanchang.karana.formattedEndTime && (
              <span className="text-[10px] text-astro-secondary-text block mt-1 font-mono">
                until {todayPanchang.karana.formattedEndTime}
              </span>
            )}
          </div>
        </div>

        {/* Dynamic Authoritative Inauspicious & Auspicious Timings Warning Box (No hardcoded strings) */}
        <div className="bg-astro-secondary-surface border border-[#E2DED5] rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#52635F] shrink-0 mt-0.5" />
          <div className="text-xs text-astro-text space-y-1.5 w-full">
            <div className="flex items-center justify-between">
              <span className="font-bold text-astro-secondary">
                {isHi 
                  ? 'दैनिक काल-विभाजन एवं अशुभ काल (Authoritative Temporal Windows):' 
                  : 'Daily Temporal Windows (Calculated from Authoritative Sunrise/Sunset):'}
              </span>
              <span className="text-[10px] font-mono text-astro-secondary-text">
                Sun: {todayPanchang.rahuKalam.rawStart ? todayPanchang.evidence.dayDurationHours : 12}h day
              </span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-1 font-mono text-[11px]">
              <div>
                • Rahu Kalam: <span className="font-semibold text-astro-secondary">{todayPanchang.rahuKalam.start} – {todayPanchang.rahuKalam.end}</span>
              </div>
              <div>
                • Yamaganda: <span className="font-semibold text-astro-secondary">{todayPanchang.yamaganda.start} – {todayPanchang.yamaganda.end}</span>
              </div>
              <div>
                • Gulika Kalam: <span className="font-semibold text-astro-secondary">{todayPanchang.gulikaKalam.start} – {todayPanchang.gulikaKalam.end}</span>
              </div>
              <div>
                • Abhijit: <span className="font-semibold text-emerald-700">{todayPanchang.abhijit.start} – {todayPanchang.abhijit.end}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lagna Tailored Muhurtas */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2 border-b border-astro-border pb-3">
          <Sparkles className="w-5 h-5 text-astro-primary" />
          <h3 className="font-bold text-astro-primary font-serif text-base">
            {isHi ? `${lagnaSign} लग्न के लिए श्रेष्ठ मुहूर्त एवं गतिविधियां` : `Auspicious Muhurtas Tailored for ${lagnaSign} Lagna`}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-astro-primary font-bold text-xs uppercase tracking-wider">
              <CheckCircle2 className="w-4 h-4 text-emerald-700" />
              <span>{isHi ? 'व्यापार एवं उद्यम' : 'Business & Enterprise'}</span>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {advice.business}
            </p>
            <div className="pt-2 text-[11px] font-mono font-semibold text-astro-primary">
              Recommended Choghadiya: Amrit / Shubh
            </div>
          </div>

          <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-astro-primary font-bold text-xs uppercase tracking-wider">
              <CheckCircle2 className="w-4 h-4 text-emerald-700" />
              <span>{isHi ? 'यात्रा एवं प्रस्थान' : 'Travel & Relocation'}</span>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {advice.travel}
            </p>
            <div className="pt-2 text-[11px] font-mono font-semibold text-astro-primary">
              Recommended Choghadiya: Chal / Labh
            </div>
          </div>

          <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-astro-primary font-bold text-xs uppercase tracking-wider">
              <CheckCircle2 className="w-4 h-4 text-emerald-700" />
              <span>{isHi ? 'निवेश एवं क्रय' : 'Investment & Assets'}</span>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {advice.investment}
            </p>
            <div className="pt-2 text-[11px] font-mono font-semibold text-astro-primary">
              Recommended Choghadiya: Shubh / Amrit
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
